import React, { useContext, useRef, useState } from 'react';
import "../../../styles/admin/UserActivity.css";
import { Users } from '../../../models/User.tsx';
import { FiAlertCircle } from "react-icons/fi";
import { LuCalendarSearch } from "react-icons/lu";
import { getUserActivityByDateRange } from '../../../services/admin/UserService.tsx';
import { UserActivity, UserIPData } from '../../../models/UserActivity.tsx';
import { BiWorld } from "react-icons/bi";
import IpMapModal from './IpMapModal.tsx';
import { useUser } from '../../../context/UserContext.tsx';
import AgentContext from '../../../context/admin/Agent.tsx';
import { MoonLoader } from 'react-spinners';
import {
    adminDateInputToUtc,
    ADMIN_TIMEZONE_LABEL,
    formatAdminFiguresDateLabel,
    formatAdminFiguresDateTime,
    formatAdminFiguresTime,
    getAdminFiguresDateKey,
} from '../../../helpers/adminFiguresTime.tsx';

type IpMatchUser = {
    userName: string;
    count: number;
    lastSeen: Date;
    city?: string;
    country?: string;
};

type IpMatchRow = {
    ip: string;
    users: IpMatchUser[];
};

const normalizeIp = (ip?: string) => (ip || '').trim();

const getActivityDate = (activity: UserActivity): Date =>
    activity.dateTime instanceof Date
        ? activity.dateTime
        : activity.dateTime.toDate();

const formatIpLocation = (city?: string, country?: string) => {
    const cityLabel = city?.trim();
    const countryLabel = country?.trim();
    if (cityLabel && countryLabel) return `${cityLabel}, ${countryLabel}`;
    return cityLabel || countryLabel || '—';
};

const formatPacificTime = (date: Date) =>
    formatAdminFiguresTime(date, { second: "2-digit" });

const formatPacificDateTime = (date: Date) =>
    formatAdminFiguresDateTime(date, { second: "2-digit" });

const buildIpUserStats = (activities: UserActivity[]) => {
    const ipMap = new Map<string, Map<string, IpMatchUser>>();

    activities.forEach(activity => {
        const ip = normalizeIp(activity.IpData?.ip);
        if (!ip) return;

        const userName = activity.userName.trim().toUpperCase();
        if (!userName) return;

        if (!ipMap.has(ip)) ipMap.set(ip, new Map());
        const users = ipMap.get(ip)!;
        const lastSeen = getActivityDate(activity);
        const existing = users.get(userName);

        if (!existing) {
            users.set(userName, {
                userName,
                count: 1,
                lastSeen,
                city: activity.IpData?.city,
                country: activity.IpData?.country,
            });
            return;
        }

        existing.count += 1;
        if (lastSeen.getTime() > existing.lastSeen.getTime()) {
            existing.lastSeen = lastSeen;
            if (activity.IpData?.city) existing.city = activity.IpData.city;
            if (activity.IpData?.country) existing.country = activity.IpData.country;
        }
    });

    return ipMap;
};

const sortMatchUsers = (users: IpMatchUser[]) =>
    [...users].sort((a, b) => b.count - a.count || a.userName.localeCompare(b.userName));

const getOtherPlayersOnPlayerIps = (
    activities: UserActivity[],
    playerIps: Set<string>,
    playerName: string
): IpMatchRow[] => {
    const normalizedTarget = playerName.trim().toUpperCase();
    const ipMap = buildIpUserStats(activities);

    return Array.from(ipMap.entries())
        .filter(([ip]) => playerIps.has(ip))
        .map(([ip, users]) => ({
            ip,
            users: sortMatchUsers(
                Array.from(users.values()).filter(user => user.userName !== normalizedTarget)
            ),
        }))
        .filter(match => match.users.length > 0)
        .sort((a, b) => a.ip.localeCompare(b.ip));
};

const getGlobalIpMatch = (activities: UserActivity[]): IpMatchRow[] => {
    const ipMap = buildIpUserStats(activities);

    return Array.from(ipMap.entries())
        .map(([ip, users]) => ({
            ip,
            users: sortMatchUsers(Array.from(users.values())),
        }))
        .filter(match => match.users.length > 1)
        .sort((a, b) => b.users.length - a.users.length || a.ip.localeCompare(b.ip));
};

const UserIP = () => {
    const { user: currentUser } = useUser();
    const { allPlayers, allAgents } = useContext(AgentContext);
    const todayKey = getAdminFiguresDateKey(new Date());
    const searchGen = useRef(0);
    const [startDate, setStartDate] = useState(todayKey);
    const [endDate, setEndDate] = useState(todayKey);
    const [ipType, setIpType] = useState('webAccesLog');
    const [userIpActivity, setUserIpActivity] = useState<UserActivity[]>([]);
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState<Users[]>([]);
    const [isLoadingPlayer, setIsLoadingPlayer] = useState(false);
    const [isLoadingMatches, setIsLoadingMatches] = useState(false);
    const [noDataMessage, setNoDataMessage] = useState('');
    const [isUserSelected, setIsUserSelected] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [selectedIp, setSelectedIp] = useState<UserIPData | null>(null);
    const [ipMatches, setIpMatches] = useState<IpMatchRow[]>([]);
    const [matchActivity, setMatchActivity] = useState<UserActivity[]>([]);

    const searchableAccounts = [...allPlayers, ...allAgents];

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);

        if (value.trim() === '') {
            setIsUserSelected(false);
            setSearchResults([]);
            return;
        }

        setIsUserSelected(false);
        const results = filterUsers(searchableAccounts, value).slice(0, 10);
        setSearchResults(results);
    };

    const filterUsers = (users: Users[], queryText: string) => {
        const text = queryText.toLowerCase().trim();
        const seen = new Set<string>();

        return users.filter(user => {
            const userName = (user.userName || "").toLowerCase();
            if (!userName || seen.has(userName)) return false;
            seen.add(userName);

            const name = (user.name || "").toLowerCase();
            const pin = String(user.pin || "").toLowerCase();

            return (
                userName.includes(text) ||
                name.includes(text) ||
                pin.includes(text)
            );
        });
    };

    const resolveSearchUserName = (rawName: string) => {
        const text = rawName.trim();
        if (!text) return null;

        const exact = searchableAccounts.find(
            user => (user.userName || '').toLowerCase() === text.toLowerCase()
        );
        if (exact?.userName) return exact.userName;
        if (isUserSelected) return text;
        return null;
    };

    const findIpGeoActivity = (ip: string) =>
        matchActivity.find(activity => normalizeIp(activity.IpData?.ip) === ip && activity.IpData?.lat && activity.IpData?.lon)
        || userIpActivity.find(activity => normalizeIp(activity.IpData?.ip) === ip && activity.IpData?.lat && activity.IpData?.lon);

    const openIpMap = (ipData: UserIPData) => {
        setSelectedIp(ipData);
        setShowMap(true);
    };

    const renderIpGlobe = (ip: string, fallbackIpData?: UserIPData) => {
        const exampleActivity = findIpGeoActivity(ip);
        const ipData = exampleActivity?.IpData || fallbackIpData;
        if (!ipData?.lat || !ipData?.lon) return null;

        return (
                    <BiWorld
                        className="icon-ip"
                        style={{ cursor: "pointer" }}
                        title="View location"
                        onClick={() => openIpMap(ipData)}
                    />
        );
    };

    const userActivityHistory = async (overrideUserName?: string) => {
        const start = adminDateInputToUtc(startDate);
        const end = adminDateInputToUtc(endDate, true);

        if (start.getTime() > end.getTime()) {
            setNoDataMessage('Start date must be before end date.');
            setHasSearched(false);
            return;
        }

        const viewerName = currentUser!.userName;
        const viewerType = currentUser?.userType!;
        const gen = ++searchGen.current;

        setSearchResults([]);
        setNoDataMessage('');
        setHasSearched(true);
        setIpMatches([]);
        setMatchActivity([]);

        try {
            if (ipType === 'globalIpMatch' && !overrideUserName) {
                setUserIpActivity([]);
                setIsLoadingPlayer(false);
                setIsLoadingMatches(true);

                const activity = await getUserActivityByDateRange(
                    null,
                    viewerName,
                    viewerType,
                    start,
                    end
                );
                if (gen !== searchGen.current) return;

                setMatchActivity(activity);
                setIpMatches(getGlobalIpMatch(activity));
                if (activity.length === 0) setNoDataMessage('No data available');
                return;
            }

            const playerName = overrideUserName || resolveSearchUserName(searchText);
            if (!playerName) {
                setHasSearched(false);
                setNoDataMessage('Please select a user from the list or type an exact username.');
                return;
            }

            setSearchText(playerName);
            setIsUserSelected(true);
            setUserIpActivity([]);
            setIsLoadingPlayer(true);
            setIsLoadingMatches(true);

            const playerPromise = getUserActivityByDateRange(
                playerName,
                viewerName,
                viewerType,
                start,
                end
            );
            const visiblePromise = getUserActivityByDateRange(
                null,
                viewerName,
                viewerType,
                start,
                end
            );

            const playerActivity = await playerPromise;
            if (gen !== searchGen.current) return;

            setUserIpActivity(playerActivity);
            setIsLoadingPlayer(false);

            if (playerActivity.length === 0) {
                setNoDataMessage('No data available');
                setIsLoadingMatches(false);
                return;
            }

            const visibleActivity = await visiblePromise;
            if (gen !== searchGen.current) return;

            const playerIps = new Set(
                playerActivity
                    .map(activity => normalizeIp(activity.IpData?.ip))
                    .filter(Boolean)
            );

            setMatchActivity(visibleActivity);
            setIpMatches(getOtherPlayersOnPlayerIps(visibleActivity, playerIps, playerName));
        } catch (err) {
            if (gen !== searchGen.current) return;
            console.error(err);
            setNoDataMessage('Error loading data');
        } finally {
            if (gen === searchGen.current) {
                setIsLoadingPlayer(false);
                setIsLoadingMatches(false);
            }
        }
    };

    const openUserLog = (userName: string) => {
        setIpType('webAccesLog');
        setSearchText(userName);
        setIsUserSelected(true);
        setSearchResults([]);
        void userActivityHistory(userName);
    };

    const groupedByDate = userIpActivity.reduce((groups, activity) => {
        const dateKey = getAdminFiguresDateKey(getActivityDate(activity));
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(activity);
        return groups;
    }, {} as Record<string, UserActivity[]>);

    const groupedDateKeys = Object.keys(groupedByDate).sort((a, b) => b.localeCompare(a));

    const handleIpTypeChange = (value: string) => {
        searchGen.current += 1;
        setIpType(value);
        setSearchText('');
        setSearchResults([]);
        setIsUserSelected(false);
        setUserIpActivity([]);
        setIpMatches([]);
        setMatchActivity([]);
        setNoDataMessage('');
        setHasSearched(false);
        setIsLoadingPlayer(false);
        setIsLoadingMatches(false);
    };

    const renderMatchUsers = (users: IpMatchUser[]) => (
        <div className="ip-other-players">
            {users.map(user => (
                <div key={user.userName} className="ip-other-player">
                    <button
                        type="button"
                        className="ip-other-player-btn"
                        onClick={() => openUserLog(user.userName)}
                    >
                        {user.userName}
                    </button>
                    <span className="ip-other-player-meta">
                        {user.count} {user.count === 1 ? 'hit' : 'hits'}
                        {' · '}
                        last {formatPacificDateTime(user.lastSeen)} {ADMIN_TIMEZONE_LABEL}
                        {' · '}
                        {formatIpLocation(user.city, user.country)}
                    </span>
                </div>
            ))}
        </div>
    );

    const renderIpMatchTable = (usersHeader: string, emptyMessage: string) => (
        <table className="table table-user-activity table-ip-log">
            <thead className="header-customer">
                <tr>
                    <th className="ip-col">IP Address</th>
                    <th>{usersHeader} (PST)</th>
                </tr>
            </thead>
            <tbody className="body-customer">
                {ipMatches.length > 0 ? ipMatches.map(match => {
                    const exampleActivity = findIpGeoActivity(match.ip);
                    return (
                        <tr key={match.ip}>
                            <td className="ip-col" data-label="IP Address">
                                <span className="ip-cell">
                                    <span className="ip-value">{match.ip}</span>
                                    {renderIpGlobe(match.ip, exampleActivity?.IpData)}
                                </span>
                            </td>
                            <td data-label={`${usersHeader} (PST)`}>{renderMatchUsers(match.users)}</td>
                        </tr>
                    );
                }) : (
                    hasSearched && !isLoadingMatches && (
                        <tr>
                            <td colSpan={2}>{emptyMessage}</td>
                        </tr>
                    )
                )}
            </tbody>
        </table>
    );

    const renderInlineLoader = (label: string) => (
        <div className="ip-inline-loader" role="status" aria-live="polite">
            <MoonLoader color="#b6cc18" loading size={42} speedMultiplier={0.85} />
            <span className="ip-inline-loader-text">{label}</span>
        </div>
    );

    return (
        <>
            <div className='container-fluid ip-tracking-page'>
                <div className='mt-2 container-body'>
                    <div className="userPendig-container ip-filters mt-1">
                        <div className="user-row ip-filter-top">
                            <div className="input-group input-group-sm flex-nowrap ip-filter-search">
                                <span className="input-group-text body-customer">Find</span>
                                <input
                                    className="inputTransaction body-customer"
                                    placeholder={
                                        ipType === 'globalIpMatch'
                                            ? 'Global IP Match does not require user'
                                            : 'Search accounts'
                                    }
                                    name="customers"
                                    value={searchText}
                                    onChange={handleSearchChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') void userActivityHistory();
                                    }}
                                    autoComplete="off"
                                    disabled={ipType === 'globalIpMatch'}
                                />

                                {searchResults.length > 0 && (
                                    <ul className="list-group overflow-auto" style={{ maxHeight: "300px" }}>
                                        {searchResults.map((user) => (
                                            <li
                                                key={user.id || user.userName}
                                                className="list-group-item"
                                                onClick={() => {
                                                    setSearchText(user.userName);
                                                    setIsUserSelected(true);
                                                    setSearchResults([]);
                                                }}
                                            >
                                                <div className='d-flex justify-content-between'>
                                                    <div>
                                                        <span className='search-username-navbar'>{user.userName}</span>
                                                        <span className='search-pin-name-navbar'>({user.pin})</span>
                                                    </div>
                                                    <div>
                                                        <span className='search-pin-name-navbar'>{user.name}</span>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="colum-pending ip-filter-type">
                                <select id="transaction" name="ipType" className="selectTransaction body-customer" value={ipType} onChange={(e) => handleIpTypeChange(e.target.value)}>
                                    <option value="webAccesLog">Web Access Log</option>
                                    <option value="globalIpMatch">Global IP Match</option>
                                </select>
                            </div>
                        </div>

                        <div className="ip-date-row">
                            <input
                                type="date"
                                id="startDate"
                                name="startDate"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="form-control"
                            />
                            <span className="ip-date-to">To</span>
                            <input
                                type="date"
                                id="endDate"
                                name="endDate"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="form-control"
                            />
                            <button className="btn-transaction ip-search-btn" onClick={() => void userActivityHistory()}>
                                <LuCalendarSearch />
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 table-responsive ip-log-tables">
                        {ipType === 'webAccesLog' ? (
                            <>
                                {isLoadingPlayer && renderInlineLoader('Loading activity...')}
                                {groupedDateKeys.map(date => (
                                    <div key={date} className="ip-day-group">
                                        <span className="date-header">
                                            {formatAdminFiguresDateLabel(adminDateInputToUtc(date), {
                                                month: "long",
                                                day: "2-digit",
                                                year: "numeric",
                                            })} ({ADMIN_TIMEZONE_LABEL})
                                        </span>
                                        <table className="table table-user-activity table-ip-log">
                                            <thead className="header-customer">
                                                <tr>
                                                    <th className="time-col">Time ({ADMIN_TIMEZONE_LABEL})</th>
                                                    <th className="ip-col">IP Address</th>
                                                    <th className="action-col">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="body-customer">
                                                {groupedByDate[date].map(activity => {
                                                    const timeDate = getActivityDate(activity);
                                                    const timeStr = formatPacificTime(timeDate);
                                                    const ip = normalizeIp(activity.IpData?.ip);

                                                    return (
                                                        <tr key={activity.id}>
                                                            <td className="time-col" data-label={`Time (${ADMIN_TIMEZONE_LABEL})`}>{timeStr}</td>
                                                            <td className="ip-col" data-label="IP Address">
                                                                <span className="ip-cell">
                                                                    <span className="ip-value">{ip || '—'}</span>
                                                                    {ip ? renderIpGlobe(ip, activity.IpData) : null}
                                                                    {!ip && activity.IpData?.lat && activity.IpData?.lon && (
                                                                        <BiWorld
                                                                            className="icon-ip"
                                                                            style={{ cursor: "pointer" }}
                                                                            title="View location"
                                                                            onClick={() => openIpMap(activity.IpData!)}
                                                                        />
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td className="action-col" data-label="Action">{activity.action}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}
                                {hasSearched && (userIpActivity.length > 0 || isLoadingMatches) && (
                                    <div className="ip-match-section ip-day-group">
                                        <span className="date-header">
                                            Other players on the same IP
                                        </span>
                                        {isLoadingMatches
                                            ? renderInlineLoader('Checking other players...')
                                            : renderIpMatchTable(
                                                'Other Players',
                                                'No other players have logged in from these IP addresses'
                                            )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {isLoadingMatches && renderInlineLoader('Loading IP matches...')}
                                {!isLoadingMatches && renderIpMatchTable(
                                    'Users',
                                    'No IP matches found'
                                )}
                            </>
                        )}
                    </div>

                    {noDataMessage && (
                        <div className="no-data-message-user">
                            <div className="no-data-message-content">
                                <FiAlertCircle className="no-data-icon" />
                                <span className='messagin-data-ip'>{noDataMessage}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showMap && selectedIp?.lat !== undefined && selectedIp?.lon !== undefined && (
                <IpMapModal
                    show={showMap}
                    onClose={() => setShowMap(false)}
                    ipData={{
                        lat: selectedIp.lat,
                        lon: selectedIp.lon,
                        ip: selectedIp.ip,
                        city: selectedIp.city,
                        country: selectedIp.country,
                    }}
                />
            )}
        </>
    );
};

export default UserIP;
